import type { Database } from 'sql.js';
import { dispatchLocalSqlitePersistFailure } from '@core/domain/localSqlitePersistEvents.ts';
import type { LocalSqliteRuntime } from '../../local-sqlite/domain/LocalSqliteRuntime.ts';

export type { LocalSqliteConnectionHealth, LocalSqliteRuntime } from '../../local-sqlite/domain/LocalSqliteRuntime.ts';

const PERSIST_FAILURE_MESSAGE =
  'No se pudo guardar una copia local de la base de datos. Libere espacio en Almacenamiento o sincronice pronto.';

function persistFailureMessage(err: unknown): string {
  const hint = err instanceof Error ? err.message.trim() : String(err ?? '').trim();
  return hint ? `${PERSIST_FAILURE_MESSAGE} (${hint})` : PERSIST_FAILURE_MESSAGE;
}
/** Builds local sqlite runtime. */


function buildLocalSqliteRuntime(): LocalSqliteRuntime {
  let db: Database | undefined;
  let persist: () => void = () => {};
  let flushPersistImpl: () => Promise<void> = async () => {};
  let hasPendingPersistImpl: () => boolean = () => false;
  let lastError: string | undefined;

  return {
    getDb: () => db,
    notifyPersist: () => {
      try {
        persist();
      } catch (e) {
        // oxlint-disable-next-line no-console -- persist may throw if IDB/SQL fails at runtime
        console.warn('[local-sqlite] notifyPersist', e);
        dispatchLocalSqlitePersistFailure({ phase: 'notify', message: persistFailureMessage(e) });
      }
    },
    flushPersist: async () => {
      try {
        await flushPersistImpl();
      } catch (e) {
        // oxlint-disable-next-line no-console -- persist may throw if IDB/SQL fails at runtime
        console.warn('[local-sqlite] flushPersist', e);
        dispatchLocalSqlitePersistFailure({ phase: 'flush', message: persistFailureMessage(e) });
        throw e;
      }
    },
    hasPendingPersist: () => hasPendingPersistImpl(),
    setSession: (d, p, flush, hasPending) => {
      db = d;
      persist = p;
      flushPersistImpl = flush ?? (async () => {
        p();
      });
      hasPendingPersistImpl = hasPending ?? (() => false);
      lastError = undefined;
    },
    signalConnectionFailed: (message: string) => {
      lastError = message;
    },
    getConnectionHealth: () => ({
      ready: db != null,
      lastError,
    }),
    clearSession: () => {
      db = undefined;
      persist = () => {};
      flushPersistImpl = async () => {};
      hasPendingPersistImpl = () => false;
      lastError = undefined;
    },
    resetForTests: () => {
      db = undefined;
      persist = () => {};
      flushPersistImpl = async () => {};
      hasPendingPersistImpl = () => false;
      lastError = undefined;
    },
  };
}

/** Factory for the browser SQLite session container (injectable into auth and repositories). */
export class LocalSqliteRuntimeFactory {
  /** Returns a fresh in-memory runtime handle (pair with `setSession`). */
  static create(): LocalSqliteRuntime {
    return buildLocalSqliteRuntime();
  }
}
